export type ContentShape = {
	/** What the content turned out to be, which is the first thing to know about a failure. */
	kind: 'empty' | 'draftjs' | 'json-not-draftjs' | 'not-json';
	/** Characters, so a size-related failure is visible without carrying the content. */
	length: number;
	/** Distinct DraftJS block types, sorted. Absent for content that is not DraftJS. */
	blockTypes?: string[];
	/** Distinct DraftJS entity types, sorted. Absent for content that is not DraftJS. */
	entityTypes?: string[];
};

const distinctSorted = (values: unknown[]): string[] =>
	[...new Set(values.filter((value): value is string => typeof value === 'string' && value !== ''))].sort();

/**
 * A structural description of a piece of rich-text content, safe to attach to an error report.
 *
 * **Never includes the content itself.** A task description is customer data —
 * names, rates, client detail — and an error report is not a place to put it. Block
 * and entity types are the schema rather than the text, and they are what actually
 * identifies a failing construct: "this broke on a task with an atomic block and a
 * JIRA_TABLE entity" is the whole diagnosis, and the prose adds nothing to it.
 *
 * `kind` separates the three cases a bare try/catch used to conflate. Content that is
 * not JSON is Jira-synced HTML and entirely expected; JSON that is not a DraftJS
 * document is odd but harmless; a genuine DraftJS document that fails to convert is
 * the only one of the three that is a defect.
 */
export const describeContentShape = (content: string | null | undefined): ContentShape => {
	if (!content) return {kind: 'empty', length: 0};

	let parsed: unknown;
	try {
		parsed = JSON.parse(content);
	} catch {
		return {kind: 'not-json', length: content.length};
	}

	const document = parsed as {blocks?: unknown; entityMap?: Record<string, {type?: string} | null> | null};
	if (!Array.isArray(document?.blocks)) {
		return {kind: 'json-not-draftjs', length: content.length};
	}

	return {
		kind: 'draftjs',
		length: content.length,
		blockTypes: distinctSorted((document.blocks as ({type?: string} | null)[]).map(block => block?.type)),
		entityTypes: distinctSorted(Object.values(document.entityMap ?? {}).map(entity => entity?.type)),
	};
};
